/* eslint-disable react-hooks/exhaustive-deps */
import { Navigate, Outlet } from 'react-router-dom';
import { getUserDetails } from '../utils/orgName';
import moment from 'moment';
import { useLogout } from '../hooks';
import { useAppSelector } from '../redux/hooks';

import ROLES from '../utils/roles';
import useProfileDetails from '../hooks/useProfileDetails';
import _ from 'lodash';
import useOrgSettings from '../hooks/useOrgSettings';
import { isLoggingOut } from '../utils/logoutInProgress';
import { isTransparentAuth } from '../auth/authMode';
import { canRenewSession, freshAccessToken, hasSession, isLeaving, useSessionTokens } from '../auth/session';
import AppLoader from '../components/AppLoader';
import { isCandidateRoute, useHiringCandidateLanding } from './HiringCandidateRedirect';

const SecuredRoutes = () => {
  const tokens = localStorage.getItem('tokens');
  const isLoggedIn = localStorage.getItem('isAuthenticated') || false;
  const { doLogout } = useLogout();
  const role = localStorage.getItem('role');
  const { exp } = getUserDetails();
  const currentTime = moment();
  const expiryTime = moment.unix(exp);
  const userData = useAppSelector((state) => state.userProfile.data);
  const { ftue, firstName } = useProfileDetails();
  const isOrgSettings = useOrgSettings();
  const candidate = useHiringCandidateLanding();
  // Re-render when the session is renewed (or ends) so the expiry check below sees the new token.
  useSessionTokens();

  const checkSession = () => {
    if (currentTime.isAfter(expiryTime)) {
      // TRANSPARENT AUTH: an expired access token is not the end of the session while it can be
      // renewed. Renew (session.ts shows the sign-in screen if that is refused, and keeps trying
      // through a passing failure); the renewal re-renders this guard.
      if (isTransparentAuth() && canRenewSession()) {
        freshAccessToken();
        return <AppLoader />;
      }
      // TRANSPARENT AUTH: the tokens are gone but the rest is still here — another tab of this
      // browser is clearing the storage key by key (a sign-in, a sign-out, or a session the API
      // ended: that tab says so, and the session module answers with the reason). Nothing to
      // decide here; the next change re-renders this guard.
      if (isTransparentAuth() && !hasSession()) return null;
      doLogout();
      return null; // Return null since the navigation will handle the rendering
    } else {
      if (userData && ftue && role !== ROLES.platAdmin) {
        if (role === ROLES.orgAdmin && !isOrgSettings && firstName !== '') {
          return <Outlet />;
        }
        // A Hiring candidate never goes through the member first-time setup
        // (DA-5042 C-9): it is a learner view, and it would ask them for
        // attributes and a member type, which is what makes someone look like
        // a member. Their own onboarding is the round's. Routes/Redirect.tsx
        // sends them here, so bouncing them away would loop.
        if (candidate === 'candidate' || isCandidateRoute()) return <Outlet />;
        if (candidate === 'asking') return null;
        window.location.replace('/');
        return null;
      }
      return <Outlet />; // Render the nested routes
    }
  };
  // On the way out to Auth0 the storage is already empty: sending the user to /login now would start a
  // new sign-in and cancel the logout (utils/logoutInProgress.ts).
  if (isLoggingOut()) return null;
  // TRANSPARENT AUTH: the same on the way to the sign-in screen after the API ended the session
  // (the address carries the reason; a /login of this guard's own would replace it).
  if (isLeaving()) return null;
  return role && (tokens || isLoggedIn) ? checkSession() : <Navigate to="/login" />;
};

export default SecuredRoutes;
